import { Stack, Group, Text, Card, Badge } from '@repletoryreach/sdk-ui';
import { Alert, WhatsAppPreview } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { parseComponents } from '../../../lib/template-components';
import type { TemplateProblem } from '../../../lib/template-validation';
import { isMediaFormat } from '../components/HeaderBuilder';
import { normaliseName, resizeSamples, highestPlaceholder, type TemplateDraft } from './draft';

/**
 * Which field a problem belongs to.
 *
 * A list of rules is not a list of fixes. "Placeholders must start at {{1}}"
 * tells somebody what is true, not where to go, and the whole point of the
 * review step is that every line names the field to open and the change to
 * make. The step to jump back to comes from the same map.
 */
const FIELD_OF: Array<[RegExp, { key: string; step: number }]> = [
  [/^NAME_/, { key: 'templates.wizard.name', step: 1 }],
  [/^HEADER_/, { key: 'templates.header.label', step: 2 }],
  [/^FOOTER_/, { key: 'templates.wizard.footer', step: 2 }],
  [/^BUTTON/, { key: 'templates.wizard.buttons', step: 2 }],
  [/^(BODY|VARIABLES|MARKETING)/, { key: 'templates.wizard.body', step: 2 }],
  [/^COMPONENT/, { key: 'templates.wizard.content.title', step: 2 }]
];

function fieldFor(code: string): { key: string; step: number } {
  for (const [pattern, field] of FIELD_OF) if (pattern.test(code)) return field;
  return { key: 'templates.wizard.content.title', step: 2 };
}

/**
 * Step 4: everything that is wrong, in one list, then the two ways out.
 *
 * Save as draft keeps it here and changes nothing at Meta. Submit is a one-way
 * door: the template enters a review queue, becomes immutable, and takes a day
 * or more to come back. The two are deliberately not the same button.
 */
export function ReviewStep({
  draft,
  components,
  problems,
  advisories,
  librarySource,
  onGoToStep
}: {
  draft: TemplateDraft;
  components: string;
  problems: TemplateProblem[];
  advisories: TemplateProblem[];
  /** `meta` when the wording is Meta's own and unchanged, so review is faster. */
  librarySource: 'meta' | 'starter' | null;
  onGoToStep: (step: number) => void;
}) {
  const t = useT();
  const parsed = parseComponents(components);
  const header = parsed.find((c) => c.type === 'HEADER');
  const body = parsed.find((c) => c.type === 'BODY');
  const footer = parsed.find((c) => c.type === 'FOOTER');
  const buttons = parsed.find((c) => c.type === 'BUTTONS')?.buttons ?? [];
  const samples = resizeSamples(draft.bodySamples, highestPlaceholder(draft.body));

  return (
    <div className="rr-tplwiz__content">
      <Stack gap="lg">
        <Card withBorder>
          <Stack gap={10}>
            <Text role="inputLabel">{t('templates.wizard.review.summary')}</Text>
            <div className="rr-tplwiz__summary">
              <Summary label={t('templates.wizard.name')} value={<code className="rr-templates__code">{normaliseName(draft.name)}</code>} />
              <Summary label={t('common.language')} value={draft.language} />
              <Summary
                label={t('templates.columns.category')}
                value={<Badge size="xs" variant="light">{t(`templates.category.${draft.category}`, draft.category)}</Badge>}
              />
            </div>
          </Stack>
        </Card>

        {problems.length === 0 ? (
          <Alert color="success">{t('templates.wizard.review.clean')}</Alert>
        ) : (
          <Alert color="error" role="alert">
            <Stack gap={8}>
              <Text role="bodySmall" fw={650}>{t('templates.wizard.review.problems', { count: problems.length })}</Text>
              <ul className="rr-tplwiz__problems">
                {problems.map((p) => {
                  const field = fieldFor(p.code);
                  return (
                    <li key={`${p.code}-${JSON.stringify(p.vars ?? {})}`}>
                      <button type="button" className="rr-tplwiz__problem" onClick={() => onGoToStep(field.step)}>
                        <span className="rr-tplwiz__problem-field">{t(field.key)}</span>
                        <span>{t(p.key, p.vars)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Stack>
          </Alert>
        )}

        {/* Not refusals. A placeholder with no sample is submitted with one the
            platform invents, and the review then reads that instead of a real
            example, which is worth saying without blocking the button. */}
        {advisories.length > 0 && (
          <Alert color="warning">
            <Stack gap={8}>
              <Text role="bodySmall" fw={650}>{t('templates.wizard.review.advice')}</Text>
              <ul className="rr-tplwiz__problems">
                {advisories.map((p) => {
                  const field = fieldFor(p.code);
                  return (
                    <li key={p.code}>
                      <button type="button" className="rr-tplwiz__problem" onClick={() => onGoToStep(field.step)}>
                        <span className="rr-tplwiz__problem-field">{t(field.key)}</span>
                        <span>{t(p.key, p.vars)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Stack>
          </Alert>
        )}

        {librarySource === 'meta' && (
          <Alert color="info">{t('templates.wizard.review.metaLibrary')}</Alert>
        )}

        <Alert color="info">{t('templates.wizard.review.submitWarning')}</Alert>
      </Stack>

      <div className="rr-tplwiz__preview">
        <Text role="inputLabel">{t('templates.drawer.preview')}</Text>
        <WhatsAppPreview
          chrome
          businessName={t('templates.wizard.preview.business')}
          composerLabel={t('templates.wizard.preview.composer')}
          header={header
            ? {
              text: header.text,
              format: header.format,
              mediaUrl: isMediaFormat(header.format) ? header.example?.header_handle?.[0] : undefined
            }
            : null}
          body={body?.text}
          footer={footer?.text}
          buttons={buttons}
          emptyBodyLabel={t('templates.wizard.preview.empty')}
          renderPlaceholder={(position) => (
            <span className="rr-tplwiz__ph">
              {samples[Number(position) - 1]?.trim() || `{{${position}}}`}
            </span>
          )}
        />
      </div>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rr-tplwiz__summary-item">
      <span className="rr-templates__stamp-label">{label}</span>
      <Group gap={6} align="center">{value}</Group>
    </div>
  );
}
